import { Box } from '@mui/material';
import { reformatItemsWithKey } from 'core/utils/reformatItemsWithKey';
import {
  AutomationActionType,
  AutomationTaskInput
} from 'generated-graphql/types';
import {
  RulesetForm,
  RulesetFormHandle
} from 'modules/analyse/components/RulesetForm';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import { SubmittedRulesetFormValues } from 'modules/analyse/types/RulesetFormValues';
import { ToastType, useToast } from 'services/toast';
import { Ref, useMemo } from 'react';

type Props = {
  requirements: AnalysisRequirementInput[];
  automationTasks: AutomationTaskInput[];
  autofillDefaultTemplate: boolean;
  templateId?: string;
  onNext: ({
    groupName,
    requirements,
    automationTasks
  }: {
    groupName?: string;
    requirements: AnalysisRequirementInput[];
    automationTasks: AutomationTaskInput[];
  }) => void;
  onTemplateChange?: (templateId: string) => void;
  formRef?: Ref<RulesetFormHandle>;
};

export const RequirementsSetupStep = ({
  requirements,
  automationTasks,
  autofillDefaultTemplate,
  onNext,
  templateId,
  onTemplateChange,
  formRef
}: Props) => {
  const { showToast } = useToast();

  const memoizedInitialValues = useMemo(
    () => ({
      analysisRequirements: reformatItemsWithKey(requirements),
      actionAutomationTasks:
        automationTasks?.filter(
          (task) =>
            task.action.type !== AutomationActionType.UpdateCandidateStatus
        ) || [],
      ruleAutomationTasks:
        automationTasks?.filter(
          (task) =>
            task.action.type === AutomationActionType.UpdateCandidateStatus
        ) || []
    }),
    [requirements, automationTasks]
  );

  const onFormSubmitted = async (
    values: SubmittedRulesetFormValues,
    isSubmittedFromModal?: boolean
  ) => {
    const automationTasks = [
      ...values.ruleAutomationTasks,
      ...values.actionAutomationTasks
    ].map<AutomationTaskInput>((item) => {
      return {
        action: item.action,
        requirementConditions: item.requirementConditions
      };
    });

    // NOTE: This means the user is creating a ruleset
    if (isSubmittedFromModal && !values.groupName) {
      showToast({
        message: 'Ruleset name is required',
        type: ToastType.Error
      });
      return;
    }

    onNext({
      groupName: values.groupName,
      requirements: values.analysisRequirements,
      automationTasks
    });

    return;
  };

  return (
    <Box>
      <RulesetForm
        showRulesetNameInput={false}
        onFormSubmitted={onFormSubmitted}
        initialValues={memoizedInitialValues}
        saveAsRulesetInitialValue={false}
        autofillDefaultTemplate={autofillDefaultTemplate}
        templateId={templateId}
        onTemplateChange={onTemplateChange}
        formRef={formRef}
        isEditable
      />
    </Box>
  );
};
